import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { TransactionTypes } from '@lib/constants/transaction.constant';
import { PermissionService } from '@lib/services/permission.service';

@Injectable({
    providedIn: 'root',
})
export class Utils {
    constructor(private permissionService: PermissionService) { }

    getTransactionType(txnType: string): string {
        if (txnType === '') return 'N/A';
        return TransactionTypes[txnType as keyof typeof TransactionTypes];
    }

    getPermissions(permissionNames: string | string[]): boolean {
        const allPermissions = this.permissionService.getPermissions();
        if (!allPermissions) return false;

        const requiredPermissions = Array.isArray(permissionNames) ? permissionNames : [permissionNames];

        return requiredPermissions.some((permission) => allPermissions.includes(permission));
    }

    allPermissionsRequired(permissionNames: string[]): boolean {
        const allPermissions = this.permissionService.getPermissions();
        if (!allPermissions) return false;

        const requiredPermissions = Array.isArray(permissionNames) ? permissionNames : [permissionNames];

        return requiredPermissions.every((permission) => allPermissions.includes(permission));
    }

    eitherPermissionRequired(permissionNames: string | string[]): boolean {
        const allPermissions = this.permissionService.getPermissions();
        if (!allPermissions) return false;

        const requiredPermissions = Array.isArray(permissionNames) ? permissionNames : [permissionNames];
        if (requiredPermissions.length === 1) {
            return allPermissions.includes(requiredPermissions[0]);
        }
        return requiredPermissions.some((permission) => allPermissions.includes(permission));
    }

    getCurrencyByTenantName(currency: any[], tenantName?: string) {
        if (!tenantName) {
            return currency;
        }

        return currency.filter((curr) => !curr.renderTenants || curr.renderTenants.includes(tenantName));
    }

    downloadFile(data: Blob) {
        const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        window.open(url);
    }

    downloadCsvFile(data: Blob) {
        const blob = new Blob([data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        window.open(url);
    }

    base64ToBlob(base64: string, contentType: string): Blob {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: contentType });
    }

    transformFormValue(value: any): any {
        const transformed = JSON.parse(JSON.stringify(value));

        const bimonthly = transformed?.credit_settings?.bimonthly_statement_on;

        if (Array.isArray(bimonthly)) {
            const allEmpty = bimonthly.every(item => item === null || item === undefined);

            if (allEmpty) {
                transformed.credit_settings.bimonthly_statement_on = null;
            }
        }

        return transformed;
    }

    bimonthlyValidator() {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            if (!value || !Array.isArray(value) || value.length !== 2) {
                return null;
            }

            const [first, second] = value;

            if (first === null || first === undefined || second === null || second === undefined) {
                return null;
            }

            if (first === second) {
                return { bimonthlyDuplicates: 'Both statement dates cannot be the same' };
            }

            if (second <= first) {
                return { bimonthlyOrder: 'Second statement date must be greater than the first' };
            }

            return null;
        }
    }
}
